import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getToken } from 'src/auth';
import { api } from 'src/api/config';
import { OverviewTab } from '../components/OverviewTab';
import { FIsTab } from '../components/FIsTab';
import { TransactionsTab } from '../components/TransactionsTab';
import { SettingsTab } from '../components/SettingsTab';
import {
  fetchPlatformMetrics,
  fetchFIMetrics,
  fetchAccountMetrics,
  fetchPaymentIssues,
} from '../services/reportingApi';

/**
 * Super Admin Dashboard Page
 * Displays platform-wide metrics, FI performance, and commission tracking
 * Now with 4-tab interface: Overview | FIs | Transactions | Settings
 */
function SuperAdminDashboardPageComponent({ token }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformMetrics, setPlatformMetrics] = useState(null);
  const [fiMetrics, setFiMetrics] = useState([]);
  const [accountMetrics, setAccountMetrics] = useState([]);
  const [financialInstitutions, setFinancialInstitutions] = useState([]);
  const [paymentIssues, setPaymentIssues] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    if (token) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, token]);

  // Fetch FI list when Settings tab is active
  useEffect(() => {
    if (activeTab === 'settings' && token && financialInstitutions.length === 0) {
      fetchFinancialInstitutions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]);

  async function loadDashboardData() {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (dateRange.start_date) params.start_date = dateRange.start_date;
      if (dateRange.end_date) params.end_date = dateRange.end_date;

      const [platform, fis, accounts, issues] = await Promise.all([
        fetchPlatformMetrics(params, token.accessToken),
        fetchFIMetrics(params, token.accessToken),
        fetchAccountMetrics(params, token.accessToken),
        fetchPaymentIssues({}, token.accessToken).catch(() => null),
      ]);

      setPlatformMetrics(platform);
      setFiMetrics(Array.isArray(fis) ? fis : []);
      setAccountMetrics(Array.isArray(accounts) ? accounts : []);
      setPaymentIssues(issues);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }

  async function fetchFinancialInstitutions() {
    try {
      const response = await fetch(`${api.hostname}/api/v1/fis/`, {
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch FIs');

      const data = await response.json();
      setFinancialInstitutions(data.results || data);
    } catch (err) {
      console.error('Failed to fetch financial institutions:', err);
      setError(err.message || 'Failed to load financial institutions');
    }
  }

  function handleDateChange(field, value) {
    setDateRange(prev => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleTabChange(event, newValue) {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue });
  }

  function handleNavigateToTab(tabName) {
    setActiveTab(tabName);
    setSearchParams({ tab: tabName });
  }

  if (loading && !platformMetrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Super Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Platform-wide metrics, commission tracking, and FI performance
        </Typography>
      </Box>

      {/* Date Range Filters */}
      <Box mb={3} display="flex" gap={2}>
        <TextField
          label="Start Date"
          type="date"
          value={dateRange.start_date}
          onChange={(e) => handleDateChange('start_date', e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={dateRange.end_date}
          onChange={(e) => handleDateChange('end_date', e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Overview" value="overview" />
          <Tab label="ISOs" value="fis" />
          <Tab label="Transactions" value="transactions" />
          <Tab label="Settings" value="settings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 'overview' && (
          <OverviewTab
            platformMetrics={platformMetrics}
            fiMetrics={fiMetrics}
            paymentIssues={paymentIssues}
            onNavigateToTab={handleNavigateToTab}
          />
        )}

        {activeTab === 'fis' && (
          <FIsTab fiMetrics={fiMetrics} />
        )}

        {activeTab === 'transactions' && (
          <TransactionsTab dateRange={dateRange} />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            financialInstitutions={financialInstitutions}
            onSaveSettings={() => {
              // Refresh FI list after saving
              fetchFinancialInstitutions();
            }}
          />
        )}
      </Box>
    </Box>
  );
}

// Connect to Redux store
const mapStateToProps = (state) => ({
  token: getToken(state),
});

export const SuperAdminDashboardPage = connect(mapStateToProps)(SuperAdminDashboardPageComponent);
